import path from 'path';

import dayjs from 'dayjs';

import { OffsetPagedResults } from '~interfaces';
import { SearchPagedParameters } from '~interfaces/requests';
import { BaseReferenceResponse, SkcOffsetPagedResponse } from '~interfaces/responses';
import { TopologyProxiedSkopeiConnectService } from '~services';

import SkcDeviceCommandEnum from '../enums/skcDeviceCommandEnum';
import Device from '../interfaces/device';
import SkcDeviceCommand from '../interfaces/skcDeviceCommand';

type SendCommandAction = Omit<SkcDeviceCommand, 'device'> | { command: string; endDate?: Date };

class SkcDevicesService extends TopologyProxiedSkopeiConnectService {
	public readonly path = 'devices';
	public readonly commandsSubPath = 'commands';
	public readonly lteCommandsSubPath = '4g';

	/**
	 * Get a list of devices
	 * @param page The number of the page
	 * @param pageSize The amount of results of the page
	 * @returns
	 */
	async getDevices({
		page = 1,
		pageSize = 10,
		deviceTypes = [2], // deviceType 2 is doorlocks in skopei connect
		...args
	}: { hardwareIds?: string[] } & SearchPagedParameters): Promise<OffsetPagedResults<Device>> {
		const { data } = await this._client.get<SkcOffsetPagedResponse<SkcDeviceResponse>>(this.path, {
			params: {
				'device-type': deviceTypes.join(','),
				'hardware-ids': args.hardwareIds?.join(','),
				'page-number': page,
				'page-size': pageSize,
				'organisation-id': args.organisationId,
				'search-query': args.searchQuery || undefined,
			},
		});

		return this.mapOffsetPagedResponse(data, SkcDevicesService.fromResponse);
	}

	async getDeviceById(id: string): Promise<Device> {
		const { data } = await this._client.get<{ data: SkcDeviceResponse }>(path.join(this.path, id));

		return SkcDevicesService.fromResponse(data.data);
	}

	async getDeviceCommands(deviceId: string, filterArray: string[] = []): Promise<string[]> {
		const { data } = await this._client.get<string[] | { data?: string[] }>(
			path.join(this.path, this.commandsSubPath, this.lteCommandsSubPath, deviceId)
		);

		if (Array.isArray(data)) {
			if (filterArray.length > 0) {
				return data.filter((command) => filterArray.includes(command));
			}
			return data;
		}

		return [];
	}

	/**
	 * Send a command to Skopei Connect - this is - from whatever reason - GET endpoint
	 * @param skcDeviceId The skopei connect id, not the hardware id
	 * @param action
	 * @returns
	 */
	async sendCommand(skcDeviceId: string, action: Omit<SkcDeviceCommand, 'device'>): Promise<null> {
		const mappedAction = (() => {
			switch (action.command) {
				case SkcDeviceCommandEnum.EnableOfficeMode:
					return {
						command: 'officeMode',
						// Get the difference with now, but round up
						metadata: dayjs(action.endDate).diff(undefined, 'minutes') + 1,
					};
				case SkcDeviceCommandEnum.DisableOfficeMode:
					return {
						command: 'lock',
					};
				default:
					return {
						command: action.command,
					};
			}
		})();

		await this._client.get(
			path.join(this.path, skcDeviceId, this.commandsSubPath, mappedAction.command),
			{
				params: {
					metadata: mappedAction.metadata,
				},
			}
		);
		return null;
	}

	/** This is the correct way to send a command to Skopei Connect having hardwareId
	 * @param hardwareId The hardware id of the device
	 * @param command The command to send
	 * @returns
	 */

	async postCommand(hardwareId: string, command: string): Promise<null> {
		await this._client.post(path.join(this.path, hardwareId, this.commandsSubPath, command));
		return null;
	}

	static fromResponse(data: SkcDeviceResponse): Device {
		const { id, name, hardwareId, deviceType, state, organisation } = data;

		return {
			id: id.toString(),
			label: name,
			hardwareId: hardwareId,
			type: deviceType,
			firmware: state.firmware !== 'unknown' ? state.firmware : undefined,
			organisation:
				organisation != null ?
					SkcDevicesService.fromBaseReferenceResponse(organisation)
				:	undefined,
		};
	}
}

// This is validated for the tripsinsights
// Different for the normal trip?
interface SkcDeviceResponse extends BaseReferenceResponse {
	description?: string;
	hardwareId: string;
	deviceType: string;
	hardwareProvider: string;
	bluetoothName: string;
	state: {
		firmware: string | 'unknown';
	};
	organisation?: BaseReferenceResponse;
}

export default SkcDevicesService;
